import { ConfigClient, ConfigCollection } from '@atlaskit/web-config-client';

import { FeatureGatesBootstrapValues } from '@/feature-gates/types';

import { DynamicConfigBootstrapValues } from '../types';

/**
 * FFS base URL — sourced from `STATSIG_FFS_BASE_URL` defined in
 * `services/wac-web/.env.{production|dev|staging|local}` and inlined at
 * build time. Defined inline (vs. switching on NODE_ENV) so the env file
 * is the single source of truth.
 */
const getFfsBaseUrl = (): string => (process.env['STATSIG_FFS_BASE_URL'] as string) ?? '';

/**
 * FFS API key — sourced from `FEATURE_GATES_KEY` defined in
 * `services/wac-web/.env.{production|dev|staging|local}` and inlined at
 * build time.
 */
const getFfsApiKey = (): string => (process.env['FEATURE_GATES_KEY'] as string) ?? '';

export type InitializeOptions = {
	/**
	 * Feature gate bootstrap values from `window.featureGateBootstrapValues`.
	 * `.identifiers` is forwarded as the FFS targeting context for the live
	 * `ConfigClient.fetch` call.
	 *
	 * Note: when SSR has injected `window.__DYNAMIC_CONFIG_BOOTSTRAP`,
	 * the client rehydrates from that payload and skips the network request
	 * entirely (so `bootstrapValues.identifiers` is unused on that path).
	 */
	bootstrapValues: FeatureGatesBootstrapValues;
};

/**
 * Singleton wrapper around `@atlaskit/web-config-client`'s `ConfigClient`.
 *
 * Supports both rendering paths:
 *
 *   • SSR (server) — call the static `fetchSsrBootstrap(bootstrapValues)`
 *     which performs an FFS fetch on the server, returns a serialized
 *     `DynamicConfigBootstrapValues` payload, and the caller injects it
 *     onto the page as `window.__DYNAMIC_CONFIG_BOOTSTRAP`.
 *
 *   • Client (browser) — call `getInstance().initialize({ bootstrapValues })`.
 *     The instance prefers `window.__DYNAMIC_CONFIG_BOOTSTRAP` (no
 *     network) and falls back to a live `ConfigClient.fetch` when missing
 *     or unparseable.
 *
 * On any failure, the singleton falls back to an empty `ConfigCollection`
 * so callers can safely consume configs without try/catch boilerplate.
 */
export class DynamicConfigClient {
	private static instance: DynamicConfigClient | undefined;
	private collection: ConfigCollection = ConfigCollection.empty();
	private initialized = false;
	private initializePromise: Promise<DynamicConfigBootstrapValues | undefined> | undefined;

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor, @typescript-eslint/no-empty-function
	private constructor() {
		// Singleton: instances are only created via `getInstance`.
	}

	public static getInstance(): DynamicConfigClient {
		if (!DynamicConfigClient.instance) {
			DynamicConfigClient.instance = new DynamicConfigClient();
		}
		return DynamicConfigClient.instance;
	}

	/** Reset the singleton — for tests only. */
	public static __resetForTests(): void {
		DynamicConfigClient.instance = undefined;
	}

	public isInitialized(): boolean {
		return this.initialized;
	}

	public getCollection(): ConfigCollection {
		return this.collection;
	}

	public async initialize(
		options: InitializeOptions,
	): Promise<DynamicConfigBootstrapValues | undefined> {
		if (this.initializePromise) {
			return this.initializePromise;
		}
		this.initializePromise = this.doInitialize(options).finally(() => {
			this.initialized = true;
		});
		return this.initializePromise;
	}

	private async doInitialize(
		options: InitializeOptions,
	): Promise<DynamicConfigBootstrapValues | undefined> {
		// 1. Prefer SSR-bootstrapped values from
		//    `window.__DYNAMIC_CONFIG_BOOTSTRAP` (set by SSR via
		//    `generateBootstrapScript` + `fetchSsrBootstrap`) —
		//    NO network request is made on this path.
		const ssrBootstrap =
			typeof window !== 'undefined' ? window.__DYNAMIC_CONFIG_BOOTSTRAP : undefined;

		if (ssrBootstrap?.payload) {
			try {
				this.collection = ConfigCollection.fromValues(ssrBootstrap.payload, { throw: true });
				return {
					payload: this.collection.toJson('minimal'),
				} as DynamicConfigBootstrapValues;
			} catch (error) {
				// Fall through to live fetch on parse failure.
				console.error(
					'DynamicConfigClient: failed to parse SSR bootstrap payload, falling back to live fetch',
					error,
				);
			}
		}

		// 2. No SSR payload or during SSR request — fall back to a live FFS fetch using the
		//    identifiers from `featureGateBootstrapValues`.
		try {
			this.collection = await ConfigClient.fetch({
				ffsBaseUrl: getFfsBaseUrl(),
				ffsApiKey: getFfsApiKey(),
				context: {
					namespace: 'bxp-web-platform_web',
					identifiers: options.bootstrapValues.identifiers,
					metadata: {},
				},
			});
			return {
				payload: this.collection.toJson('minimal'),
			} as DynamicConfigBootstrapValues;
		} catch (error) {
			console.error('DynamicConfigClient: failed to fetch dynamic configs', error);
			this.collection = ConfigCollection.empty();
			return undefined;
		}
	}
}
